import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  FiSearch, FiRefreshCw,
  FiPlay, FiPause, FiX,
} from 'react-icons/fi';
const getToken = () => localStorage.getItem('toolboxToken') || '';
const PAGE_SIZE = 10;

interface CallRecord {
  transactionId: string;
  created: string;
  callType: string;
  webhookData?: {
    call_transcript_memory?: string;
    call_extracted_info?: {
      call_outcome?: string;
      call_follow_up_date?: string;
      call_follow_up_time?: string;
    };
  };
  totalCount: number;
}

function formatLocalDate(utcStr: string): string {
  if (!utcStr) return 'N/A';
  const d = new Date(utcStr);
  const date = d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
  const time = d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
  return `${date} ${time}`;
}

function formatFollowUp(dateStr?: string): string {
  if (!dateStr) return 'N/A';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
}

function formatTime(secs: number): string {
  const m = Math.floor(secs / 60);
  const s = Math.floor(secs % 60);
  return `${m}:${s < 10 ? '0' : ''}${s}`;
}

const SonantHistoryView: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [records, setRecords] = useState<CallRecord[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [error, setError] = useState('');

  /* ── Modal state ── */
  const [showModal, setShowModal] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [audioUrl, setAudioUrl] = useState('');
  const [audioLoading, setAudioLoading] = useState(false);

  /* ── Audio player state ── */
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    fetchHistory(page);
  }, [page]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const interval = setInterval(() => {
      if (isPlaying && audioRef.current) setCurrentTime(audioRef.current.currentTime);
    }, 1000);
    return () => clearInterval(interval);
  }, [isPlaying]);

  const fetchHistory = (pageNum: number) => {
    setLoading(true);
    setError('');
    axios({
      method: 'get',
      url: `${(process.env.REACT_APP_DataFabric_Url || '')}/api/CallNotification/sonanthistory?pageNumber=${pageNum}&recordCount=${PAGE_SIZE}`,
      headers: { Authorization: `Bearer ${getToken()}` },
    })
      .then((res) => {
        setLoading(false);
        const data: CallRecord[] = res.data.data || [];
        setRecords(data);
        if (data.length > 0) setTotalRecords(data[0].totalCount);
      })
      .catch((err) => {
        setLoading(false);
        if (err.response?.status === 401) navigate('/');
        else setError('Unable to load call history.');
      });
  };

  const fetchRecording = (transactionId: string) => {
    setAudioLoading(true);
    axios({
      method: 'get',
      url: `${(process.env.REACT_APP_DataFabric_Url || '')}/api/CallNotification/callrecording/${transactionId}`,
      headers: { Authorization: `Bearer ${getToken()}` },
    })
      .then((res) => {
        setAudioLoading(false);
        setAudioUrl(res.data.data?.callRecordingURL || '');
      })
      .catch((err) => {
        setAudioLoading(false);
        if (err.response?.status === 401) navigate('/');
      });
  };

  const openModal = (record: CallRecord) => {
    setTranscript(record.webhookData?.call_transcript_memory || '');
    setAudioUrl('');
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(0);
    setShowModal(true);
    fetchRecording(record.transactionId);
  };

  const closeModal = () => {
    audioRef.current?.pause();
    setShowModal(false);
    setIsPlaying(false);
  };

  const togglePlay = () => {
    if (!audioRef.current) return;
    if (isPlaying) audioRef.current.pause();
    else audioRef.current.play();
    setIsPlaying(!isPlaying);
  };

  const filtered = records.filter((r) =>
    !search.trim() || r.callType?.toLowerCase().includes(search.toLowerCase()),
  );

  const totalPages = Math.ceil(totalRecords / PAGE_SIZE);

  const thStyle: React.CSSProperties = {
    padding: '8px 12px', textAlign: 'left', fontSize: 11, fontWeight: 600,
    color: 'var(--text-secondary)', borderBottom: '1px solid var(--border-glass)',
    whiteSpace: 'nowrap',
  };
  const tdStyle: React.CSSProperties = {
    padding: '10px 12px', fontSize: 12, color: 'var(--text-primary)',
    borderBottom: '1px solid rgba(255,255,255,0.04)',
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

      {/* ── Controls ── */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <div style={{ position: 'relative', flex: 1 }}>
          <FiSearch size={13} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-tertiary)' }} />
          <input
            type="text"
            placeholder="Search by call type..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              width: '100%', boxSizing: 'border-box',
              padding: '7px 12px 7px 30px', fontSize: 12,
              background: 'var(--surface-glass)', border: '1px solid var(--border-glass)',
              borderRadius: 8, color: 'var(--text-primary)', fontFamily: 'inherit', outline: 'none',
            }}
          />
        </div>
        <button
          onClick={() => fetchHistory(page)}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '7px 14px', borderRadius: 8, fontSize: 12, fontWeight: 500,
            background: 'rgba(255,255,255,0.06)', border: '1px solid var(--border-glass)',
            color: 'var(--text-secondary)', cursor: 'pointer', fontFamily: 'inherit',
          }}
        >
          <FiRefreshCw size={13} /> Refresh
        </button>
      </div>

      {error && (
        <div style={{
          color: '#ef4444', fontSize: 12, padding: '8px 12px', borderRadius: 8,
          background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)',
        }}>
          {error}
        </div>
      )}

      {/* ── Table ── */}
      <div style={{
        background: 'var(--surface-glass)', border: '1px solid var(--border-glass)',
        borderRadius: 12, overflow: 'hidden',
      }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'rgba(255,255,255,0.03)' }}>
                <th style={thStyle}>Call Date</th>
                <th style={thStyle}>Call Type</th>
                <th style={thStyle}>Call Outcome</th>
                <th style={thStyle}>Follow-up Date</th>
                <th style={thStyle}>Follow-up Time</th>
                <th style={{ ...thStyle, textAlign: 'center' }}>Transcript</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                [...Array(PAGE_SIZE)].map((_, i) => (
                  <tr key={i}>
                    {[...Array(6)].map((__, j) => (
                      <td key={j} style={tdStyle}>
                        <div style={{
                          height: 12, borderRadius: 4,
                          background: 'rgba(255,255,255,0.06)',
                          width: ['60%', '40%', '55%', '45%', '35%', '30%'][j],
                          animation: 'tbx-shimmer 1.5s ease-in-out infinite',
                        }} />
                      </td>
                    ))}
                  </tr>
                ))
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={6} style={{ ...tdStyle, textAlign: 'center', padding: '32px 12px', color: 'var(--text-tertiary)' }}>
                    No call records found.
                  </td>
                </tr>
              ) : (
                filtered.map((r) => (
                  <tr key={r.transactionId} style={{ transition: 'background 150ms' }}
                    onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.025)')}
                    onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                  >
                    <td style={tdStyle}>{formatLocalDate(r.created)}</td>
                    <td style={tdStyle}>
                      {/* <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                        {r.callType === 'Incoming'
                          ? <FiPhoneIncoming size={13} style={{ color: '#34d399' }} />
                          : r.callType === 'Outgoing'
                          ? <FiPhoneOutgoing size={13} style={{ color: '#60a5fa' }} />
                          : null}
                        {r.callType || 'N/A'}
                      </span> */}
                    </td>
                    <td style={{ ...tdStyle, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {r.webhookData?.call_extracted_info?.call_outcome || 'N/A'}
                    </td>
                    <td style={tdStyle}>{formatFollowUp(r.webhookData?.call_extracted_info?.call_follow_up_date)}</td>
                    <td style={tdStyle}>{r.webhookData?.call_extracted_info?.call_follow_up_time || 'N/A'}</td>
                    <td style={{ ...tdStyle, textAlign: 'center' }}>
                      {r.webhookData?.call_transcript_memory ? (
                        <button
                          onClick={() => openModal(r)}
                          style={{
                            padding: '4px 12px', borderRadius: 6, fontSize: 11, fontWeight: 600,
                            background: 'rgba(162,0,255,0.12)', border: '1px solid rgba(162,0,255,0.3)',
                            color: '#A200FF', cursor: 'pointer', fontFamily: 'inherit',
                          }}
                        >
                          View
                        </button>
                      ) : (
                        <span style={{ color: 'var(--text-tertiary)', fontSize: 11 }}>No transcript</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* ── Pagination ── */}
        {!loading && totalPages > 1 && (
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '10px 16px', borderTop: '1px solid var(--border-glass)',
          }}>
            <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>
              Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, totalRecords)} of {totalRecords}
            </span>
            <div style={{ display: 'flex', gap: 4 }}>
              <button
                disabled={page === 1}
                onClick={() => setPage((p) => p - 1)}
                style={{
                  padding: '4px 10px', borderRadius: 6, fontSize: 12,
                  background: page === 1 ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.07)',
                  border: '1px solid var(--border-glass)',
                  color: page === 1 ? 'var(--text-tertiary)' : 'var(--text-secondary)',
                  cursor: page === 1 ? 'default' : 'pointer', fontFamily: 'inherit',
                }}
              >
                ← Prev
              </button>
              <button
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
                style={{
                  padding: '4px 10px', borderRadius: 6, fontSize: 12,
                  background: page >= totalPages ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.07)',
                  border: '1px solid var(--border-glass)',
                  color: page >= totalPages ? 'var(--text-tertiary)' : 'var(--text-secondary)',
                  cursor: page >= totalPages ? 'default' : 'pointer', fontFamily: 'inherit',
                }}
              >
                Next →
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── Transcript Modal ── */}
      {showModal && (
        <div
          onClick={closeModal}
          style={{
            position: 'fixed', inset: 0, zIndex: 1000,
            background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'var(--bg-primary, #0f0f1a)', border: '1px solid var(--border-glass)',
              borderRadius: 16, width: '60vw', maxWidth: 760, height: '78vh',
              display: 'flex', flexDirection: 'column', overflow: 'hidden',
            }}
          >
            {/* Modal header */}
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '14px 20px', borderBottom: '1px solid var(--border-glass)',
            }}>
              <div style={{ color: 'var(--text-primary)', fontSize: 14, fontWeight: 700 }}>Call Transcript</div>
              <button
                onClick={closeModal}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: 'var(--text-tertiary)', display: 'flex', alignItems: 'center',
                }}
              >
                <FiX size={18} />
              </button>
            </div>

            {/* Transcript body */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '14px 20px', display: 'flex', flexDirection: 'column', gap: 8 }}>
              {transcript.split('\n').filter(Boolean).map((line, i) => {
                const isBot = line.startsWith('BOT:');
                return (
                  <div key={i} style={{ display: 'flex', justifyContent: isBot ? 'flex-start' : 'flex-end' }}>
                    <div style={{
                      maxWidth: '72%', padding: '8px 12px', borderRadius: 10, fontSize: 12, lineHeight: 1.65,
                      background: isBot ? 'rgba(96,165,250,0.12)' : 'rgba(52,211,153,0.12)',
                      border: isBot ? '1px solid rgba(96,165,250,0.2)' : '1px solid rgba(52,211,153,0.2)',
                      color: 'var(--text-primary)',
                    }}>
                      <span style={{ fontWeight: 700, color: isBot ? '#60a5fa' : '#34d399', marginRight: 4 }}>
                        {isBot ? 'BOT:' : 'HUMAN:'}
                      </span>
                      {line.replace(/^(BOT:|HUMAN:)/, '').trim()}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Audio player */}
            <div style={{
              borderTop: '1px solid var(--border-glass)',
              padding: '12px 20px', background: 'rgba(255,255,255,0.02)',
            }}>
              {audioLoading ? (
                <div style={{ textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 12, padding: '6px 0' }}>
                  Loading audio…
                </div>
              ) : audioUrl ? (
                <>
                  <audio
                    ref={audioRef}
                    src={audioUrl}
                    onTimeUpdate={() => audioRef.current && setCurrentTime(audioRef.current.currentTime)}
                    onLoadedMetadata={() => audioRef.current && setDuration(audioRef.current.duration)}
                    onEnded={() => setIsPlaying(false)}
                  />
                  <input
                    type="range" min={0} max={duration || 0} value={currentTime}
                    onChange={(e) => {
                      const t = parseFloat(e.target.value);
                      if (audioRef.current) audioRef.current.currentTime = t;
                      setCurrentTime(t);
                    }}
                    style={{ width: '100%', marginBottom: 8, accentColor: '#A200FF' }}
                  />
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                      {formatTime(currentTime)} / {formatTime(duration)}
                    </span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <button onClick={() => { if (audioRef.current) { audioRef.current.currentTime = Math.max(0, currentTime - 10); } }}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', display: 'flex' }}>
                        {/* <FiSkipBack size={16} /> */}
                      </button>
                      <button onClick={togglePlay}
                        style={{
                          width: 34, height: 34, borderRadius: '50%', border: 'none', cursor: 'pointer',
                          background: '#A200FF', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                        {isPlaying ? <FiPause size={15} /> : <FiPlay size={15} />}
                      </button>
                      <button onClick={() => { if (audioRef.current) { audioRef.current.currentTime = Math.min(duration, currentTime + 10); } }}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', display: 'flex' }}>
                        {/* <FiSkipForward size={16} /> */}
                      </button>
                    </div>
                    <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>&nbsp;</span>
                  </div>
                </>
              ) : (
                <div style={{ textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 12, padding: '6px 0' }}>
                  No recording available
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SonantHistoryView;

